import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ArrowRight, Play, ChevronDown, Clock } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export const HeroSection = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.3], [0, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden hex-pattern" data-testid="hero-section">
      <div className="absolute top-20 right-0 w-96 h-96 bg-[#E7A541]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#FDF6E9] rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} style={{ y, opacity }}>
            <motion.span variants={fadeUp} className="inline-block px-4 py-2 bg-[#FDF6E9] text-[#E7A541] rounded-full text-sm font-semibold tracking-wider uppercase mb-6">
              EduPlan - Une École Un Plan
            </motion.span>
            
            <motion.h1 variants={fadeUp} className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#29282B] leading-tight mb-6">
              Gagnez <span className="gradient-text">2h42 de cours</span> par classe chaque semaine
            </motion.h1>
            
            <motion.p variants={fadeUp} className="text-lg text-[#29282B]/70 mb-8 max-w-lg">
              Vos élèves perdent 5 à 10 minutes à s'installer à chaque cours. Avec EduPlan, ils savent exactement où s'asseoir. Résultat : +7,3 minutes de cours récupérées par heure.
            </motion.p>
            
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 mb-8">
              <a href="#contact" className="inline-flex items-center justify-center px-8 py-4 bg-[#E7A541] text-white rounded-full font-semibold text-lg hover:bg-[#D4913A] transition-all duration-300 btn-shine animate-pulse-glow" data-testid="hero-cta-primary">
                Demander une démo <ArrowRight className="ml-2" size={20} />
              </a>
              <a href="#video" className="inline-flex items-center justify-center px-8 py-4 text-[#29282B] font-medium hover:text-[#E7A541] transition-colors duration-300" data-testid="hero-cta-secondary">
                <Play className="mr-2" size={20} /> Voir EduPlan en action
              </a>
            </motion.div>
            
            <motion.div variants={fadeUp} className="flex flex-wrap gap-6 text-sm text-[#29282B]/60">
              <span className="flex items-center gap-2">
                <Clock className="text-[#E7A541]" size={18} />
                <strong className="text-[#E7A541]">+7,3 min</strong> par cours
              </span>
              <span className="flex items-center gap-2">
                <Check className="text-[#E7A541]" size={18} />
                Conforme RGPD
              </span>
              <span className="flex items-center gap-2">
                <Check className="text-[#E7A541]" size={18} />
                Hébergé en Europe
              </span>
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="relative">
            <div className="animate-float">
              <div className="macbook-frame max-w-xl mx-auto">
                <div className="macbook-screen aspect-[16/10]">
                  <img
                    src="/eduplan-screens/editeur de plan de classe vie-scolaire.png"
                    alt="EduPlan - Éditeur de plan de classe"
                    className="w-full h-full object-cover object-top" />

                </div>
              </div>
              <div className="macbook-base max-w-xl mx-auto" />
            </div>
            
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute -top-4 -right-4 bg-white rounded-xl shadow-xl p-4">

              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#E7A541] flex items-center justify-center">
                  <span className="!font-bold !text-lg text-white">+3h</span>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#29282B]">par semaine</div>
                  <div className="text-xs text-[#29282B]/60">par classe</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }} className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <ChevronDown className="text-[#E7A541]" size={32} />
      </motion.div>
    </section>);

};

export default HeroSection;